import { useEffect } from 'react';

function useDocMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute('content', description);
    return () => {
      document.title = 'Go Convert Documents — Free Online Document Converter';
      if (m) m.setAttribute('content', 'Convert any file format to PDF, Word, Excel, PowerPoint, and more. Fast, secure, no registration required.');
    };
  }, [title, description]);
}

export function Privacy() {
  useDocMeta(
    'Privacy Policy | Go Convert Documents',
    'How Go Convert Documents handles your files, data, cookies, and personal information. Files auto-delete after 1 hour.'
  );
  return (
    <article className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-display font-bold text-4xl md:text-5xl">Privacy Policy</h1>
      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500 mt-2">
        Last updated: February 2026
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">What we collect</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Go Convert Documents is designed to be used anonymously. We do not require an account, email, or credit card. When you use a conversion tool we temporarily process:
      </p>
      <ul className="list-disc pl-6 mt-3 space-y-1 text-zinc-700">
        <li>The file(s) you upload — kept only long enough to perform the conversion.</li>
        <li>Basic technical logs (IP address, timestamp, HTTP method) for security and abuse-prevention.</li>
      </ul>

      <h2 className="font-display font-semibold text-2xl mt-10">File retention</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Every uploaded and converted file is automatically deleted from our servers <strong>one hour</strong> after processing. We do not read, index, share, or resell the content of your documents.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Cookies</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        We store a small piece of data in your browser&apos;s local storage to remember your recently used tools. This never leaves your device. We do not use tracking cookies.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Third-party services</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        The service is hosted on secure cloud infrastructure over HTTPS. If we ever add advertising (e.g. Google AdSense) this section will be updated with the exact vendors and links to their privacy policies.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Your rights</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Because we do not store personal data or file contents beyond the 1-hour window, there is nothing to export or delete on request. If you believe a file has been retained by mistake, contact us and we will investigate.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Contact</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        For privacy questions, please use the <a href="/contact" className="underline font-semibold">contact page</a>.
      </p>
    </article>
  );
}

export function Terms() {
  useDocMeta(
    'Terms of Service | Go Convert Documents',
    'Terms and conditions for using Go Convert Documents — free online document converter.'
  );
  return (
    <article className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-display font-bold text-4xl md:text-5xl">Terms of Service</h1>
      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500 mt-2">
        Last updated: February 2026
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Acceptable use</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        By using Go Convert Documents you agree not to upload content that is illegal, infringes copyright, contains malware, or that you do not have the right to process. We may block IP addresses that abuse the service.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Service availability</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        The service is offered on a best-effort basis, free of charge. We do not guarantee uninterrupted availability or that every conversion will preserve the original layout perfectly.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Liability</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Go Convert Documents is provided &quot;as is&quot; without warranty of any kind. We are not liable for any loss of data, business, or profits arising from use of the service. Always keep a copy of your original files.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Changes</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        We may update these terms occasionally. Continued use of the service constitutes acceptance of the updated terms.
      </p>
    </article>
  );
}

export function About() {
  useDocMeta(
    'About | Go Convert Documents',
    'Go Convert Documents is a free online toolbox for converting PDF, Word, Excel, PowerPoint, images and running OCR — with privacy at the core.'
  );
  return (
    <article className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-display font-bold text-4xl md:text-5xl">About Go Convert Documents</h1>
      <p className="text-zinc-700 mt-6 text-lg leading-relaxed">
        Go Convert Documents is a free online toolbox that helps you convert, merge, split, compress, protect and OCR your documents in seconds — without an account, without software installs, and without your files being stored beyond one hour.
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">What we believe</h2>
      <ul className="list-disc pl-6 mt-3 space-y-2 text-zinc-700 leading-relaxed">
        <li><strong>Document tools should be free.</strong> Converting a Word file to PDF is table-stakes computing and should never require a subscription.</li>
        <li><strong>Privacy is the default.</strong> Your files stay on our servers just long enough to be processed. They are never read, indexed, resold or trained on.</li>
        <li><strong>The web is the platform.</strong> No downloads, no installers, no OS restrictions.</li>
      </ul>

      <h2 className="font-display font-semibold text-2xl mt-10">What we support</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        26 conversion and utility tools across 4 categories — PDF ⇄ Office, PDF ⇄ Images, image ⇄ image, and PDF utilities (merge, split, compress, rotate, protect, unlock, watermark, OCR).
      </p>

      <h2 className="font-display font-semibold text-2xl mt-10">Contact us</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Have a feature request, bug report or partnership idea? <a href="/contact" className="underline font-semibold">Get in touch</a>.
      </p>
    </article>
  );
}

export function Contact() {
  useDocMeta(
    'Contact | Go Convert Documents',
    'Get in touch with the Go Convert Documents team for feedback, bug reports, or partnership requests.'
  );
  return (
    <article className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-display font-bold text-4xl md:text-5xl">Contact</h1>
      <p className="text-zinc-700 mt-6 text-lg leading-relaxed">
        We would love to hear from you. Send us feedback, feature requests or bug reports and we will get back within 2 business days.
      </p>

      <div className="mt-8 border-2 border-black bg-white p-6 brutal-shadow">
        <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-500">Email</p>
        <p className="font-display text-2xl mt-2">
          <a href="mailto:hello@goconvertdocuments.com" className="underline underline-offset-4 hover:no-underline" data-testid="contact-email">
            hello@goconvertdocuments.com
          </a>
        </p>
      </div>

      <h2 className="font-display font-semibold text-2xl mt-10">Response times</h2>
      <ul className="list-disc pl-6 mt-3 space-y-1 text-zinc-700 leading-relaxed">
        <li>Bug reports: within 24 hours</li>
        <li>Feature requests: within 2 business days</li>
        <li>Press &amp; partnerships: within 3 business days</li>
      </ul>
    </article>
  );
}
